// Module-level state
let pendingMessages = new Map(); // messageId -> { resolve, reject, timeout }
let listeners = new Map(); // event -> [callbacks]

function handleIncomingMessage(event) {
  const data = event.data || {};
  const { messageId, payload, type } = data;

  // Handle response to a pending request
  if (messageId && pendingMessages.has(messageId)) {
    const { resolve, reject, timeout, detailed } = pendingMessages.get(messageId);
    clearTimeout(timeout);
    pendingMessages.delete(messageId);

    // Normalize across the new and old DQMP response shapes:
    //   NEW: { errorsLog: string[], networkLog: DqmpNetworkRequest[] }
    //   OLD: { error: string | { message }, debug: { networkRequests, handlerDurationMs } }
    // A non-empty errorsLog (or an old single `error`) means the call failed.
    let errorsLog;
    if (Array.isArray(data.errorsLog)) {
      errorsLog = data.errorsLog.filter(e => typeof e === 'string' && e.length > 0);
    } else if (data.error) {
      errorsLog = [typeof data.error === 'object' ? (data.error.message || 'Unknown error') : String(data.error)];
    } else {
      errorsLog = [];
    }
    // Old-format `debug.networkRequests` is intentionally ignored — only the new top-level networkLog is surfaced.
    const networkLog = Array.isArray(data.networkLog) ? data.networkLog : [];
    const errorString = errorsLog.length > 0 ? errorsLog.join('\n') : null;

    if (detailed) {
      // sendDetailed: always resolve so caller gets the network/error logs
      resolve({ payload: payload ?? null, error: errorString, errorsLog, networkLog });
    } else {
      // send: preserve existing behavior
      if (errorString) {
        reject(new Error(errorString));
      } else {
        resolve(payload);
      }
    }
    return;
  }

  // Handle event-based messages
  if (type && listeners.has(type)) {
    const callbacks = listeners.get(type);
    callbacks.forEach(callback => {
      callback(payload);
    });
  }
}

function generateMessageId() {
  return Math.random().toString(36).substring(2, 15);
}

// Auto-initialize listener when module loads
window.addEventListener('message', handleIncomingMessage);

export async function send(type, payload, timeout = 120000) { 
  return new Promise((resolve, reject) => {
    const messageId = generateMessageId();

    const timeoutId = setTimeout(() => {
      if (pendingMessages.has(messageId)) {
        pendingMessages.delete(messageId);
        reject(new Error(`Request timed out: ${type}`));
      }
    }, timeout);

    pendingMessages.set(messageId, {
      resolve,
      reject,
      timeout: timeoutId,
      detailed: false
    });

    window.parent.postMessage({
      source: '__DASHQUILL_MESSAGES__',
      type,
      payload,
      messageId
    }, '*');
  });
}

export async function sendDetailed(type, payload, timeout = 120000) {
  return new Promise((resolve, reject) => {
    const messageId = generateMessageId();

    const timeoutId = setTimeout(() => {
      if (pendingMessages.has(messageId)) {
        pendingMessages.delete(messageId);
        resolve({ payload: null, error: `Request timed out: ${type}`, debug: null });
      }
    }, timeout);

    pendingMessages.set(messageId, {
      resolve,
      reject,
      timeout: timeoutId,
      detailed: true
    });

    window.parent.postMessage({
      source: '__DASHQUILL_MESSAGES__',
      type,
      payload,
      messageId
    }, '*');
  });
}

// Modal chrome control — fire-and-forget, unlike every other DQMP call. Nobody is waiting on
// it, and an extension predating the message answers with an "Unknown message type" error that
// send() turns into a rejection, so a failure here is expected and must never surface.
//
// Gated on being embedded because at top level window.parent IS window: the message would be
// delivered straight back to handleIncomingMessage, match its own messageId and "succeed" with
// the request echoed as the response. The short timeout bounds a host that never answers at
// all, which would otherwise hold a pending entry for the full two minutes.
const MODAL_SETTINGS_TIMEOUT_MS = 5000;

export function updateModalSettings(settings) {
  if (window.parent === window) return;
  send('DQMP_UPDATE_MODAL_SETTINGS', { settings }, MODAL_SETTINGS_TIMEOUT_MS).catch(() => {});
}

export function addEventListener(event, callback) {
  if (!listeners.has(event)) {
    listeners.set(event, []);
  }
  listeners.get(event).push(callback);
}

export function removeEventListener(event, callback) {
  if (!listeners.has(event)) return;
  listeners.set(event, listeners.get(event).filter(cb => cb !== callback));
}
